import { Injectable } from '@angular/core';
import { ENV } from 'environments/environment';
import { CONSTANTS } from 'environments/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { SessionService } from 'app/components/common/session/session.service';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CatalogueService {

  private catalogueProperties: BehaviorSubject<Object> = new BehaviorSubject<Object>({});
  private eventData: BehaviorSubject<Object> = new BehaviorSubject<Object>({});
  private ensureDataRequest$: Observable<any> | null = null;
  private ensureDataRequestEventCode: string | null = null;

  constructor(private http: HttpClient, private sessionService: SessionService) { }

  setData(data) {
    this.sessionService.set('eventData', data, false);
    this.sessionService.set('eventData_' + data['eventName'], data, true);
    this.eventData.next(data);
  }

  getData(): Observable<Object> {
    return this.eventData.asObservable();
  }

  getProperty(property: string): any {
    let data = this.eventData.getValue();
    // AM-7804: el BehaviorSubject arranca en {}, nunca en null, por lo que la
    // comprobación anterior (data === null) hacía que no se recuperara nunca del
    // storage. Además SessionService.get devuelve la cadena JSON, así que hay que
    // parsearla antes de leer la propiedad.
    if (data === null || Object.keys(data || {}).length === 0) {
      const stored = this.sessionService.get('eventData', false);
      const parsed = this.parseStoredData(stored);
      if (parsed) {
        data = parsed;
        this.setData(parsed);
      }
    }
    return data?.[property] || null;
  }

  private parseStoredData(stored: any): Object | null {
    if (!stored) {
      return null;
    }
    if (typeof stored !== 'string') {
      return Object.keys(stored).length ? stored : null;
    }
    try {
      const parsed = JSON.parse(stored);
      const value = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      return value && Object.keys(value).length ? value : null;
    } catch (e) {
      return null;
    }
  }
  
  getDataProperties(): Observable<Object> {
    return this.catalogueProperties.asObservable();
  }

  setDataProperties(newData: Object): void {
    this.sessionService.set('catalogueProperties', newData);
    this.catalogueProperties.next(newData);
  }

  getPropertyProperties(property) {
    return this.getDataProperties()[property] || null;
  }

  getProfiles(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_PROFILES + '/' + dataIn.catalogueId + '/reports/exhibitorProfiles';

    return this.http.get(url);

  }

  getHalls(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_HALLS + '?language=' + dataIn.language;

    return this.http.get(url);

  }

  updateProperties(dataIn) {

    let dataPut = dataIn.dataPut;

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.UPDATE_PROPERTIES +  '/' + dataIn.catalogueId + '/catalogueLanguageProperties/saveAndUpdate?language=' + dataIn.language;

    return this.http.put(url, dataPut);
  }
  

  getActives(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_ACTIVES +  '/odsActive?eventName=' + dataIn.eventName +'&language=' + dataIn.language;

    return this.http.get(url, dataIn);

  }


  getAll(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_ALL +  '/odsProperties/getAllOds?language=' + dataIn.language;

    return this.http.get(url, dataIn);
  }


  findByEventName(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.FIND_BY_EVENT_NAME + '?eventCode=' + dataIn.eventCode;

    return this.http.get(url);

  }

  ensureDataLoaded(eventCode: string): Observable<any> {

    let cachedData = this.eventData.getValue();

    if (cachedData && cachedData['sapCode'] && cachedData['eventName'] === eventCode) {
      return of(cachedData);
    }

    if (this.ensureDataRequest$ && this.ensureDataRequestEventCode === eventCode) {
      return this.ensureDataRequest$;
    }

    this.ensureDataRequestEventCode = eventCode;
    this.ensureDataRequest$ = this.findByEventName({ eventCode: eventCode }).pipe(
      tap(data => {
        this.setData(data);
        this.ensureDataRequest$ = null;
        this.ensureDataRequestEventCode = null;
      }),
      catchError(error => {
        this.ensureDataRequest$ = null;
        this.ensureDataRequestEventCode = null;
        return throwError(error);
      }),
      shareReplay(1)
    );

    return this.ensureDataRequest$;

  }


  getSocialNetwork(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_SOCIAL_NETWORK + '/socialNetworks?eventName=' + dataIn.event;

    return this.http.get(url, dataIn);
  }

  /*
    EXAMPLE URL: https://ecataloguestg-api.cwsfirabarcelona.com/v1/catalogues/96/exhibitors/198955/hierarchicalProperties?language=es_ES
  */
  getHierarchicalProperties(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_HIERARCHICAL_PROPERTIES + '/' + dataIn.catalogueId + '/exhibitors/' + dataIn.exhibitorId + '/hierarchicalProperties';
    
    return this.http.get(url, dataIn);
  }

  /*
    EXAMPLE URL: https://ecataloguestg-api.cwsfirabarcelona.com/v1/catalogues/96/countriesInUse?language=es_ES
  */
  getCountriesInUse(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_COUNTRIES_IN_USE + '/' + dataIn.catalogueId + '/countriesInUse';

    return this.http.get(url, dataIn);
  }

  /*
    EXAMPLE URL: https://ecataloguestg-api.cwsfirabarcelona.com/v1/files/search/byCatalogueAndProduct?catalogue_id=96&product_id=38720&language=es_ES
  */
  getFilesByCatalogueAndProduct(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_FILES_BY_CATALOGUE_AND_PRODUCT + '/search/byCatalogueAndProduct?catalogue_id=' + dataIn.catalogueId + '&product_id=' + dataIn.productId;

    return this.http.get(url, dataIn);
  }

  /*
    EXAMPLE URL: https://ecataloguestg-api.cwsfirabarcelona.com/v1/catalogues/socialNetworks?eventName=barcelonawineweek2022&language=es_ES
  */
  getSocialNetworksByEvent(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_SOCIAL_NETWORKS_BY_EVENT + '/socialNetworks?eventName=' + dataIn.event;

    return this.http.get(url, dataIn);
  }

  /*
    EXAMPLE URL: https://ecataloguestg-api.cwsfirabarcelona.com/v1/catalogueUsers/resource/favorite?eventName=barcelonawineweek2022&language=es_ES
  */
  getFavoriteResources(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_FAVORITE_RESOURCES + '/favorite?eventName=' + dataIn.event;

    return this.http.get(url);
  }

  getCatalogueProperties(catalogueId) {
    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_PROFILES + '/' + catalogueId + '/' + 'catalogueProperties';
    return this.http.get(url);
  }

  publishAllExhibitors(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.PUBLISH_ALL_EXHIBITORS + dataIn.catalogueId + '/publishedAllExhibitors';

    return this.http.put(url, dataIn);

  }

  unpublishAllExhibitors(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.UNPUBLISH_ALL_EXHIBITORS + dataIn.catalogueId + '/unpublishedAllExhibitors';

    return this.http.put(url, dataIn);

  }

  createExhibitor(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.CREATE_EXHIBITOR + dataIn.catalogueId + '/exhibitors';

    return this.http.post(url, dataIn);

  }


  getCatalogueLanguageProperties(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_CATALOGUE_LANGUAGE_PROPERTIES + dataIn.catalogueId + '/catalogueLanguageProperties';

    return this.http.get(url);

  }

  catalogueLanguageSaveAndUpdate(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.CATALOGUE_LANGUAGE_SAVE_AND_UPDATE + dataIn.catalogueId + '/catalogueLanguageProperties/saveAndUpdate';

    let dataPut = dataIn.literals;

    return this.http.put(url, dataPut);

  }

  updateRRSS(dataIn) {

    let dataPut = dataIn.dataPut;

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.UPDATE_PROPERTIES +  '/' + dataIn.catalogueId + '/catalogueDetails?language=' + dataIn.language;

    return this.http.put(url, dataPut);
  }


  saveFile(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.SAVE_FILE + '/' + dataIn.catalogueId + '/catalogueDetails/generateFile?language=' + dataIn.language;;

    return this.http.post(url, dataIn);
  }


  getCatalogueMultiEvents(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.GET_CATALOGUE_MULTI_EVENTS  + '/' + dataIn.catalogueId + '/cataloguesMultiEvents';

    return this.http.get(url, dataIn);
  }

  getTranslations(dataIn, redisData) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.TRANSLATIONS.GET_TRANSLATIONS + '/' + redisData;

    return this.http.post(url, dataIn);
  }

  updateImagesColorsBanner(dataIn) {

    let dataPost = dataIn.dataPost ?? dataIn.dataPost;

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.UPDATE_IMAGES_COLORS_BANNER + '/updateCatalogueImageAndColor?language=' + dataIn.language;

    return this.http.put(url, dataPost);
  }

  uploadExhibitors(file, dataIn) {
    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.CATALOGUE.CREATE_EXHIBITOR + dataIn.catalogueId + '/exhibitors/upload';
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(url, formData);
  }

}
